/*
 * Copyright 2025 JieYang(aletme).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import type { SvgLoaderInstance, SvgLoaderOptions, SvgModule } from "./types";

// Global SVG loader
export class GlobalSvgLoader implements SvgLoaderInstance {
  private static instance: GlobalSvgLoader;
  private cache = new Map<string, string>();
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private debugMode = false;
  private customSvgModules: SvgModule | null = null;

  // Get singleton instance
  static getInstance(): GlobalSvgLoader {
    if (!GlobalSvgLoader.instance) {
      GlobalSvgLoader.instance = new GlobalSvgLoader();
    }
    return GlobalSvgLoader.instance;
  }

  // Set custom SVG modules
  setSvgModules(svgModules: SvgModule): void {
    this.customSvgModules = svgModules;
    this.debugLog("Custom SVG modules set");
  }

  // Clear custom SVG modules
  clearSvgModules(): void {
    this.customSvgModules = null;
    this.debugLog("Custom SVG modules cleared");
  }

  // Initialize - optionally preload all SVG files and optionally pass SVG modules
  async initialize(options?: SvgLoaderOptions): Promise<void> {
    options = options || {
      preloadAll: false,
      debugMode: false,
      svgModules: undefined,
    };
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (options.debugMode !== undefined) {
      this.setDebugMode(options.debugMode);
    }

    // If SVG modules are passed, set them
    if (options.svgModules) {
      this.setSvgModules(options.svgModules);
    }

    if (options.preloadAll) {
      this.initializationPromise = this._loadAllSvgs();
    } else {
      // Only mark as initialized, do not preload any files
      this.initializationPromise = Promise.resolve();
    }

    try {
      await this.initializationPromise;
      this.isInitialized = true;
    } catch (error) {
      console.warn(
        "SVG Loader initialization failed, but continuing...",
        error
      );
      this.isInitialized = true; // Mark as initialized to avoid repeated attempts
    }
  }

  // Load all SVG files
  private async _loadAllSvgs(): Promise<void> {
    try {
      // Get all SVG files - using universal glob pattern
      const svgModules = this._getSvgModules();
      const svgFiles = Object.keys(svgModules);

      // Load all SVG files in parallel
      const loadPromises = svgFiles.map(async filePath => {
        try {
          const fileName = filePath.split("/").pop()?.replace(".svg", "") || "";
          const svgModule = (await svgModules[filePath]()) as string;
          const response = await fetch(svgModule);

          if (response.ok) {
            const svgContent = await response.text();
            this.cache.set(fileName, svgContent);
          }
        } catch (error) {
          console.error(`Failed to load SVG: ${filePath}`, error)
        }
      });

      await Promise.all(loadPromises);
    } catch (error) {
      console.error('Failed to initialize SVG loader:', error)
    }
  }

  // Get SVG modules - supports different build environments
  private _getSvgModules(): SvgModule {
    // Prioritize custom SVG modules
    if (this.customSvgModules) {
      this.debugLog("Using custom SVG modules");
      return this.customSvgModules;
    }

    // In Node.js environment, return empty object
    if (typeof window === "undefined") {
      return {};
    }

    // In build environment, return empty object to avoid permission issues
    if (typeof process !== "undefined") {
      return {};
    }

    // Temporarily disable import.meta.glob to avoid build issues
    // In actual use, users need to manually configure SVG files
    return {};
  }

  // Load single SVG file on demand
  private async _loadSingleSvg(iconName: string): Promise<string> {
    try {
      // Use universal glob pattern to find and load SVG files
      const svgModules = this._getSvgModules();
      const svgFiles = Object.keys(svgModules);

      if (svgFiles.length === 0) {
        this.debugLog(
          "No SVG modules found. Make sure you're using a compatible build tool."
        );
        return "";
      }

      const targetFile = svgFiles.find(
        file =>
          file.includes(`/${iconName}.svg`) ||
          file.includes(`\\${iconName}.svg`)
      );

      if (!targetFile) {
        this.debugLog(`SVG file not found for icon: ${iconName}`);
        return "";
      }

      const svgModule = (await svgModules[targetFile]()) as string;

      if (!svgModule) {
        this.debugLog(`Failed to resolve module for: ${iconName}`);
        return "";
      }

      const response = await fetch(svgModule);

      if (response.ok) {
        const content = await response.text();
        return content;
      } else {
        this.debugLog(
          `Failed to fetch SVG for ${iconName}, status: ${response.status}`
        );
        return "";
      }
    } catch (error) {
      this.debugLog(`Failed to load ${iconName}:`, error);
      return "";
    }
  }

  // Get SVG content - supports on-demand loading
  async getSvg(iconName: string, color?: string): Promise<string> {
    this.debugLog(
      `Getting SVG: ${iconName}${color ? ` with color: ${color}` : ""}`
    );

    if (this.isFontAwesomeIcon(iconName)) {
      this.debugLog(`Skipping FontAwesome icon: ${iconName}`);
      return "";
    }

    // If already in cache, return directly
    if (this.hasSvg(iconName)) {
      this.debugLog(`Found SVG in cache: ${iconName}`);
      let svgContent = this.cache.get(iconName) || "";
      if (color && svgContent) {
        svgContent = this._applyColorToSvg(svgContent, color);
      }
      return svgContent;
    }

    // Load on demand
    this.debugLog(`Loading SVG on demand: ${iconName}`);
    const svgContent = await this._loadSingleSvg(iconName);
    if (svgContent) {
      this.cache.set(iconName, svgContent);
      this.debugLog(`Cached SVG: ${iconName}`);
      if (color) {
        return this._applyColorToSvg(svgContent, color);
      }
      return svgContent;
    }
    return "";
  }

  // Get SVG content from cache
  getSvgCache(iconName: string, color?: string): string {
    if (!this.isInitialized) {
      console.warn('SVG Loader not initialized. Call initialize() first.')
      return "";
    }

    if (this.isFontAwesomeIcon(iconName)) {
      return "";
    }

    if (this.hasSvg(iconName)) {
      let svgContent = this.cache.get(iconName) || "";
      if (color && svgContent) {
        svgContent = this._applyColorToSvg(svgContent, color);
      }
      return svgContent;
    }

    return "";
  }

  // Check if SVG exists
  hasSvg(iconName: string): boolean {
    return this.cache.has(iconName);
  }

  // Check if it is a FontAwesome icon
  isFontAwesomeIcon(iconName: string): boolean {
    return (
      iconName.startsWith("fa-") ||
      iconName.startsWith("fas ") ||
      iconName.startsWith("far ") ||
      iconName.startsWith("fab ")
    );
  }

  // Apply color to SVG content
  private _applyColorToSvg(svgContent: string, color: string): string {
    if (!color || !svgContent) {
      return svgContent;
    }

    // Replace all fill attributes with specified color
    let modifiedSvg = svgContent.replace(/fill="[^"]*"/g, `fill="${color}"`);

    // Replace all stroke attributes with specified color
    modifiedSvg = modifiedSvg.replace(/stroke="[^"]*"/g, `stroke="${color}"`);

    // If no fill or stroke attributes, add fill attribute
    if (!modifiedSvg.includes("fill=") && !modifiedSvg.includes("stroke=")) {
      // Add fill attribute to first path, circle, rect, etc. elements
      modifiedSvg = modifiedSvg.replace(
        /<(path|circle|rect|polygon|line|polyline)([^>]*?)>/g,
        `<$1$2 fill="${color}">`
      );
    }

    return modifiedSvg;
  }

  // Get all loaded SVG names
  getLoadedSvgNames(): string[] {
    return Array.from(this.cache.keys());
  }

  // Clear cache
  clearCache(): void {
    this.cache.clear();
    this.isInitialized = false;
    this.initializationPromise = null;
  }

  // Reinitialize
  async reinitialize(
    options?: SvgLoaderOptions
  ): Promise<void> {
    options = options || {
      preloadAll: false,
      debugMode: false,
      svgModules: undefined,
    }
    this.clearCache();
    await this.initialize(options);
  }

  // Set debug mode
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  // Get debug mode status
  getDebugMode(): boolean {
    return this.debugMode;
  }

  // Debug log
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(`[SVG Loader] ${message}`, ...args);
    }
  }
}
